import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * useAdminPanel — owns ALL Admin Panel state, data fetching, and mutation
 * handlers. Returns a `panel` props bag plus a few top-level fields the
 * AdminPanel.js shell uses directly (`loading`, `activeTab`, etc.).
 *
 * Lifted out of AdminPanel.js during the Phase 2 frontend split (Wave 4).
 * Pure relocation — semantics are identical to the original inline code.
 */
export default function useAdminPanel() {
  const { user, loading: authLoading, getAuthHeader } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('founder');
  const fileInputRef = useRef(null);
  
  // State
  const [users, setUsers] = useState([]);
  const [sales, setSales] = useState([]);
  const [pendingListingSales, setPendingListingSales] = useState([]);
  const [potData, setPotData] = useState({ total_amount: 0, goal_amount: 1000000 });
  const [advertisers, setAdvertisers] = useState([]);
  const [founderSettings, setFounderSettings] = useState({
    founder_is_open: true,
    founder_seat_limit: 50,
    founder_seats_used: 0
  });
  const [tierFeatures, setTierFeatures] = useState({});
  const [editingTier, setEditingTier] = useState(null);
  const [editFeatures, setEditFeatures] = useState('');
  const [edirectoryStatus, setEdirectoryStatus] = useState(null);
  const [syncingEdir, setSyncingEdir] = useState(false);
  const [importedTransactions, setImportedTransactions] = useState([]);
  const [importLogs, setImportLogs] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [pendingVideos, setPendingVideos] = useState([]);
  const [pendingLibraryVideos, setPendingLibraryVideos] = useState([]);
  const [customTiers, setCustomTiers] = useState([]);
  const [showTierForm, setShowTierForm] = useState(false);
  const [newTier, setNewTier] = useState({
    name: '',
    slug: '',
    price: 0,
    initiation_fee: 0,
    features: [],
    tier_level: 4,
    listings_allocation: 0,
    profit_percentage: 50,
    residual_percentage: 15,
    downline_commission: 0,
    can_recruit: false,
    can_create_courses: false
  });
  const [tierFeaturesInput, setTierFeaturesInput] = useState('');
  const [pendingServices, setPendingServices] = useState([]);
  const [flaggedServices, setFlaggedServices] = useState([]);
  const [featuredMembers, setFeaturedMembers] = useState([]);
  const [selectedUserToFeature, setSelectedUserToFeature] = useState('');
  const [featuredText, setFeaturedText] = useState('');
  const [commissionSettings, setCommissionSettings] = useState({
    member_commission_percent: 75,
    upline_commission_percent: 10,
    platform_commission_percent: 15
  });
  const [savingCommission, setSavingCommission] = useState(false);
  const [promoCodes, setPromoCodes] = useState([]);
  const [promoToDelete, setPromoToDelete] = useState(null);
  const [newPromo, setNewPromo] = useState({
    code: '',
    discount_type: 'percent',
    discount_percent: 10,
    discount_amount: 0,
    max_uses: 100,
    expires_at: ''
  });
  const [selectedUser, setSelectedUser] = useState(null);
  const [userSearch, setUserSearch] = useState('');
  const [pendingFounders, setPendingFounders] = useState([]);
  const [commissionDashboard, setCommissionDashboard] = useState(null);
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [moveDownlineData, setMoveDownlineData] = useState({ member_id: '', new_referral_code: '' });

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
    } else if (!user.is_admin) {
      toast.error('Admin access required');
      navigate('/dashboard');
    } else {
      fetchAllData();
    }
  }, [user, authLoading]);

  const fetchAllData = async () => {
    try {
      const headers = getAuthHeader();
      const [usersRes, salesRes, potRes, advertRes, founderRes, tiersRes, edirRes, transRes, logsRes, videosRes, customTiersRes, libraryVideosRes, pendingServicesRes, flaggedServicesRes, featuredRes, commissionRes, promoRes, pendingListingSalesRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/users`, { headers }),
        axios.get(`${API_URL}/api/sales/admin`, { headers }),
        axios.get(`${API_URL}/api/pot/status`),
        axios.get(`${API_URL}/api/products/advertiser`),
        axios.get(`${API_URL}/api/admin/founder-settings`, { headers }),
        axios.get(`${API_URL}/api/memberships/tiers`),
        axios.get(`${API_URL}/api/admin/edirectory/status`, { headers }).catch(() => ({ data: { connected: false } })),
        axios.get(`${API_URL}/api/admin/imported-transactions?limit=50`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/admin/import-logs`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/admin/youtube-videos/pending`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/admin/custom-tiers`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/admin/video-library/pending`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/admin/scheduler/services/pending`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/admin/scheduler/services/flagged`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/featured-members`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/admin/commission-settings`, { headers }).catch(() => ({ data: { member_commission_percent: 75, upline_commission_percent: 10, platform_commission_percent: 15 } })),
        axios.get(`${API_URL}/api/admin/promo-codes`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/admin/sales/pending-verification`, { headers }).catch(() => ({ data: [] }))
      ]);
      
      setUsers(usersRes.data);
      setSales(salesRes.data);
      setPotData(potRes.data);
      setAdvertisers(advertRes.data);
      setFounderSettings(founderRes.data);
      setPendingListingSales(pendingListingSalesRes.data || []);
      // Extract features from tiers response
      const features = {};
      Object.entries(tiersRes.data).forEach(([tier, data]) => {
        features[tier] = data.features || [];
      });
      setTierFeatures(features);
      setEdirectoryStatus(edirRes.data);
      setImportedTransactions(transRes.data);
      setImportLogs(logsRes.data);
      setPendingVideos(videosRes.data);
      setCustomTiers(customTiersRes.data);
      setPendingLibraryVideos(libraryVideosRes.data);
      setPendingServices(pendingServicesRes.data);
      setFlaggedServices(flaggedServicesRes.data);
      setFeaturedMembers(featuredRes.data);
      setCommissionSettings(commissionRes.data);
      setPromoCodes(promoRes.data || []);
      
      // Fetch pending founders and commission dashboard separately
      try {
        const [pendingFoundersRes, commDashRes, payoutHistoryRes] = await Promise.all([
          axios.get(`${API_URL}/api/admin/pending-founders`, { headers }),
          axios.get(`${API_URL}/api/admin/commissions`, { headers }),
          axios.get(`${API_URL}/api/admin/payouts/history`, { headers }).catch(() => ({ data: [] }))
        ]);
        setPendingFounders(pendingFoundersRes.data || []);
        setCommissionDashboard(commDashRes.data);
        setPayoutHistory(payoutHistoryRes.data || []);
      } catch (e) {
        console.warn('Failed to fetch pending founders or commissions:', e);
      }
    } catch (error) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifySale = async (saleId, approve) => {
    try {
      await axios.put(
        `${API_URL}/api/sales/verify`,
        { sale_id: saleId, approve },
        { headers: getAuthHeader() }
      );
      toast.success(approve ? 'Sale approved!' : 'Sale rejected');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to process sale');
    }
  };

  const handleVerifyListingSale = async (saleId) => {
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/sales/${saleId}/verify`,
        {},
        { headers: getAuthHeader() }
      );
      const platform = res.data?.platform_amount?.toFixed(2);
      toast.success(`Sale verified — ${res.data?.credited_recipients || 0} recipient(s) credited; $${platform || '0'} to platform.`);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verify failed');
    }
  };

  const handleCancelListingSale = async (saleId) => {
    const note = window.prompt('Cancellation reason (optional, e.g. "Customer cancelled subscription"):', '');
    if (note === null) return;  // user hit Cancel on the prompt
    if (!window.confirm('Mark this sale as CANCELLED? Any prior commission credits will be reversed.')) return;
    try {
      await axios.post(
        `${API_URL}/api/admin/sales/${saleId}/cancel`,
        { cancellation_note: note || '' },
        { headers: getAuthHeader() }
      );
      toast.success('Sale cancelled — credits reversed.');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Cancel failed');
    }
  };

  const handleUnverifyListingSale = async (saleId) => {
    if (!window.confirm('Reverse this verification? Recipients will be debited.')) return;
    try {
      await axios.post(
        `${API_URL}/api/admin/sales/${saleId}/unverify`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success('Verification reversed.');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Unverify failed');
    }
  };

  const toggleFoundingMember = async (userId) => {
    try {
      await axios.post(
        `${API_URL}/api/admin/users/${userId}/toggle-founding`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success('Founder status updated');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const toggleFounderEnrollment = async () => {
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/founder-settings/toggle`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success(response.data.message);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to toggle founder enrollment');
    }
  };

  const updateFounderSettings = async (e) => {
    e.preventDefault();
    try {
      await axios.put(
        `${API_URL}/api/admin/founder-settings`,
        { founder_seat_limit: founderSettings.founder_seat_limit },
        { headers: getAuthHeader() }
      );
      toast.success('Founder settings updated');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to update founder settings');
    }
  };

  const updatePot = async (e) => {
    e.preventDefault();
    try {
      await axios.put(
        `${API_URL}/api/admin/pot`,
        { total_amount: potData.total_amount, goal_amount: potData.goal_amount },
        { headers: getAuthHeader() }
      );
      toast.success('Millionaire pot updated');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to update pot');
    }
  };

  // Commission Settings Management
  const updateCommissionSettings = async (e) => {
    e.preventDefault();
    
    const memberPct = parseFloat(commissionSettings.member_commission_percent);
    const uplinePct = parseFloat(commissionSettings.upline_commission_percent);
    const platformPct = parseFloat(commissionSettings.platform_commission_percent);
    
    if (memberPct + uplinePct + platformPct !== 100) {
      toast.error(`Member (${memberPct}%) + Upline (${uplinePct}%) + Platform (${platformPct}%) must equal 100%`);
      return;
    }
    
    setSavingCommission(true);
    try {
      await axios.put(
        `${API_URL}/api/admin/commission-settings`,
        {
          member_commission_percent: memberPct,
          upline_commission_percent: uplinePct,
          platform_commission_percent: platformPct
        },
        { headers: getAuthHeader() }
      );
      toast.success('Commission settings updated');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update commission settings');
    } finally {
      setSavingCommission(false);
    }
  };

  // User Management Functions
  const suspendUser = async (userId, suspend = true) => {
    try {
      await axios.put(
        `${API_URL}/api/admin/users/${userId}/suspend`,
        { is_suspended: suspend },
        { headers: getAuthHeader() }
      );
      toast.success(suspend ? 'User suspended' : 'User unsuspended');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const archiveUser = async (userId, archive = true) => {
    try {
      const path = archive ? 'archive' : 'unarchive';
      await axios.post(
        `${API_URL}/api/admin/users/${userId}/${path}`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success(archive ? 'User archived — hidden from non-admin views.' : 'User unarchived.');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const permanentlyDeleteUser = async (userId, userName) => {
    if (!window.confirm(`PERMANENTLY DELETE ${userName || 'this user'} and ALL their data?\n\nThis cannot be undone. To hide a user without deleting their history, use Archive instead.`)) return;
    if (!window.confirm('Are you absolutely sure? This will erase courses, bookings, leads, payouts, etc.')) return;
    try {
      await axios.delete(
        `${API_URL}/api/admin/users/${userId}`,
        { headers: getAuthHeader() }
      );
      toast.success('User permanently deleted.');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const changeUserTier = async (userId, newTier) => {
    try {
      await axios.put(
        `${API_URL}/api/admin/users/${userId}/tier`,
        { membership_tier: newTier },
        { headers: getAuthHeader() }
      );
      toast.success(`User tier changed to ${newTier}`);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change tier');
    }
  };

  // Promo Code Management
  const createPromoCode = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API_URL}/api/admin/promo-codes`,
        newPromo,
        { headers: getAuthHeader() }
      );
      toast.success('Promo code created');
      setNewPromo({ code: '', discount_type: 'percent', discount_percent: 10, discount_amount: 0, max_uses: 100, expires_at: '' });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create promo code');
    }
  };

  const deletePromoCode = async (codeId) => {
    if (!codeId) return;
    try {
      await axios.delete(`${API_URL}/api/admin/promo-codes/${codeId}`, { headers: getAuthHeader() });
      toast.success('Promo code deleted');
      setPromoToDelete(null);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete promo code');
      setPromoToDelete(null);
    }
  };

  // Filter users by search
  const filteredUsers = users.filter(u => 
    userSearch === '' || 
    u.name?.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.email?.toLowerCase().includes(userSearch.toLowerCase())
  );

  // Tier Features Management
  const startEditingTier = (tier) => {
    setEditingTier(tier);
    setEditFeatures(tierFeatures[tier]?.join('\n') || '');
  };

  const saveTierFeatures = async () => {
    if (!editingTier) return;
    
    const featuresArray = editFeatures.split('\n').filter(f => f.trim());
    
    try {
      await axios.put(
        `${API_URL}/api/admin/tier-features`,
        { tier: editingTier, features: featuresArray },
        { headers: getAuthHeader() }
      );
      toast.success(`${editingTier} features updated`);
      setEditingTier(null);
      setEditFeatures('');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to update features');
    }
  };

  // Directory Sync
  const syncEdirectory = async () => {
    setSyncingEdir(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/edirectory/sync-data`,
        {},
        { headers: getAuthHeader() }
      );
      if (response.data.config_synced) {
        toast.success('Directory sync completed');
      } else {
        toast.warning('Sync completed with errors');
      }
      fetchAllData();
    } catch (error) {
      toast.error('Directory sync failed');
    } finally {
      setSyncingEdir(false);
    }
  };

  // CSV Import
  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }

    setImporting(true);
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API_URL}/api/admin/import-transactions`,
        formData,
        {
          headers: {
            ...getAuthHeader(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setImportResult(response.data);
      
      if (response.data.transactions_created > 0) {
        toast.success(`Imported ${response.data.transactions_created} transactions`);
      } else if (response.data.unmatched_emails?.length > 0) {
        toast.warning(`No transactions imported. ${response.data.unmatched_emails.length} emails not matched.`);
      } else {
        toast.info('Import completed - no new transactions');
      }
      
      fetchAllData();
    } catch (error) {
      toast.error('Import failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const downloadSampleCSV = () => {
    const csv = `referral_code,email,amount,paid,date
1M2G4XLY,john@example.com,1500.00,yes,2025-02-10
8KQZ3VWN,jane@example.com,2500.50,yes,2025-02-09
5TP7RBCD,bob@example.com,750.00,no,2025-02-08`;
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sample_transactions.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // YouTube Video Approval
  const handleVideoReview = async (videoId, approve, displayOnLanding = false, rejectionReason = '') => {
    try {
      await axios.put(
        `${API_URL}/api/admin/youtube-videos/${videoId}/review`,
        { approve, display_on_landing: displayOnLanding, rejection_reason: rejectionReason },
        { headers: getAuthHeader() }
      );
      toast.success(approve ? 'Video approved!' : 'Video rejected');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to review video');
    }
  };

  // Video Library Approval
  const handleLibraryVideoReview = async (videoId, approve, displayOnLanding = false, rejectionReason = '') => {
    try {
      await axios.put(
        `${API_URL}/api/admin/video-library/${videoId}/review`,
        { approve, display_on_landing: displayOnLanding, rejection_reason: rejectionReason },
        { headers: getAuthHeader() }
      );
      toast.success(approve ? 'Video approved!' : 'Video rejected');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to review video');
    }
  };

  // Batch Approve All Pending Videos
  const handleBatchApproveVideos = async () => {
    if (!window.confirm(`Approve all ${pendingVideos.length + pendingLibraryVideos.length} pending videos?`)) return;
    
    try {
      // Approve YouTube videos
      for (const video of pendingVideos) {
        await axios.put(
          `${API_URL}/api/admin/youtube-videos/${video.id}/review`,
          { approve: true, display_on_landing: false },
          { headers: getAuthHeader() }
        );
      }
      
      // Approve library videos
      for (const video of pendingLibraryVideos) {
        await axios.put(
          `${API_URL}/api/admin/video-library/${video.id}/review`,
          { approve: true, display_on_landing: false },
          { headers: getAuthHeader() }
        );
      }
      
      toast.success('All videos approved!');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to batch approve videos');
    }
  };

  // Custom Tier Management
  const handleCreateTier = async (e) => {
    e.preventDefault();
    if (!newTier.name || !newTier.slug) {
      toast.error('Name and slug are required');
      return;
    }

    try {
      const tierData = {
        ...newTier,
        features: tierFeaturesInput.split('\n').filter(f => f.trim())
      };
      await axios.post(`${API_URL}/api/admin/custom-tiers`, tierData, { headers: getAuthHeader() });
      toast.success('Custom tier created!');
      setShowTierForm(false);
      setNewTier({
        name: '', slug: '', price: 0, initiation_fee: 0, features: [], tier_level: 4,
        listings_allocation: 0, profit_percentage: 50, residual_percentage: 15,
        downline_commission: 0, can_recruit: false, can_create_courses: false
      });
      setTierFeaturesInput('');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create tier');
    }
  };

  const handleDeleteTier = async (tierId) => {
    if (!confirm('Are you sure you want to delete this tier? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/custom-tiers/${tierId}`, { headers: getAuthHeader() });
      toast.success('Tier deleted');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete tier');
    }
  };

  const handleApproveService = async (serviceId) => {
    try {
      await axios.post(`${API_URL}/api/admin/scheduler/services/${serviceId}/approve`, {}, { headers: getAuthHeader() });
      toast.success('Service approved');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to approve service');
    }
  };

  const handleFlagService = async (serviceId) => {
    const reason = prompt('Enter the reason for flagging this service:');
    if (!reason) return;
    try {
      await axios.post(`${API_URL}/api/admin/scheduler/services/${serviceId}/flag?reason=${encodeURIComponent(reason)}`, {}, { headers: getAuthHeader() });
      toast.success('Service flagged');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to flag service');
    }
  };

  const handleDeleteService = async (serviceId) => {
    if (!confirm('Are you sure you want to delete this service?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/scheduler/services/${serviceId}`, { headers: getAuthHeader() });
      toast.success('Service deleted');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete service');
    }
  };

  const handleAddFeaturedMember = async () => {
    if (!selectedUserToFeature) {
      toast.error('Please select a user');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/admin/featured-members`, {
        user_id: selectedUserToFeature,
        display_order: featuredMembers.length,
        featured_text: featuredText
      }, { headers: getAuthHeader() });
      toast.success('Member featured successfully');
      setSelectedUserToFeature('');
      setFeaturedText('');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to feature member');
    }
  };

  const handleRemoveFeaturedMember = async (featuredId) => {
    if (!confirm('Remove this member from featured?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/featured-members/${featuredId}`, { headers: getAuthHeader() });
      toast.success('Member removed from featured');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to remove featured member');
    }
  };

  // Loading / auth-redirect handling is done by the consuming AdminPanel
  // shell — this hook always returns the same shape so React can rely on
  // a stable hook order across renders.

  const pendingSales = sales.filter(s => s.status === 'pending');
  const founderSeatsRemaining = founderSettings.founder_seat_limit - founderSettings.founder_seats_used;

  // -------- props bag for extracted tab components ----------
  // Each tab destructures only what it uses; passing the full bag means
  // adding/renaming a state slice doesn't ripple through 15 prop lists.
  const panel = {
    // navigation / utilities
    user, navigate, getAuthHeader, fetchAllData, fileInputRef,
    // users / membership
    users, setUsers, userSearch, setUserSearch, filteredUsers,
    pendingFounders, founderSettings, setFounderSettings,
    founderSeatsRemaining, updateFounderSettings,
    toggleFounderEnrollment, toggleFoundingMember,
    suspendUser, archiveUser, permanentlyDeleteUser, changeUserTier,
    // sales & commissions
    sales, pendingSales, pendingListingSales,
    handleVerifySale, handleVerifyListingSale, handleUnverifyListingSale,
    handleCancelListingSale,
    commissionDashboard,
    // promo codes
    promoCodes, newPromo, setNewPromo, createPromoCode, deletePromoCode,
    promoToDelete, setPromoToDelete,
    // pot
    potData, setPotData, updatePot,
    // videos / advertisers / approvals / downline
    advertisers,
    pendingVideos, pendingLibraryVideos,
    handleBatchApproveVideos, handleVideoReview, handleLibraryVideoReview,
    moveDownlineData, setMoveDownlineData,
    // featured members
    featuredMembers, selectedUserToFeature, setSelectedUserToFeature,
    featuredText, setFeaturedText,
    handleAddFeaturedMember, handleRemoveFeaturedMember,
    // tier features
    tierFeatures, editingTier, setEditingTier, editFeatures, setEditFeatures,
    startEditingTier, saveTierFeatures,
    // custom tiers
    customTiers, showTierForm, setShowTierForm, newTier, setNewTier,
    tierFeaturesInput, setTierFeaturesInput, handleCreateTier, handleDeleteTier,
    // scheduler admin
    pendingServices, flaggedServices,
    handleApproveService, handleFlagService, handleDeleteService,
    // edirectory / csv
    edirectoryStatus, syncingEdir, syncEdirectory, importedTransactions,
    importing, importResult, handleFileSelect, downloadSampleCSV,
    // admin payouts (inline section in AdminPanel.js shell)
    payoutHistory,
  };

  return {
    // top-level shell needs
    user, navigate, loading, activeTab, setActiveTab,
    pendingFounders,
    pendingVideos, pendingLibraryVideos,
    promoToDelete, setPromoToDelete, deletePromoCode,
    // the rest is bundled as the props bag for tabs
    panel,
  };
}
